import { VVVSignUp } from "@/api/vvv";
import ZapIcon from "@/assets/svg/creator-shield/zap.svg";
import Footer from "@/components/creatorshield/Footer";
import ChannelLinks from "@/components/signup/ChannelLinks";
import PersonalInformation from "@/components/signup/PersonalInformation";
import Sign from "@/components/signup/Sign";
import ToastModal from "@/components/ToastModal";
import { creatorshieldContract } from "@/utils/creatorshieldContract";
import {
  alphabetChecker,
  isValidEmail,
  isValidUrl,
} from "@/utils/inputChecker";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function CreatorshieldEsign() {
  const headingRef = useScrollAnimation({ threshold: 0.2 });
  
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    socials: {
      youtube: "",
      tiktok: "",
      facebook: "",
      instagram: "",
      twitchTv: "",
      douyin: "",
      kuaishou: "",
      rumble: "",
      others: "",
    },
    signature: "",
    tos: false,
    contentId: false,
    informationAccurate: false,
  });

  const handleUserDataInput = (e) => {
    if (typeof e === "string") {
      setUserData((prev) => ({
        ...prev,
        country: e,
      }));
      return;
    }

    if (typeof e === "boolean") {
      setUserData((prev) => ({ ...prev, tos: e }));
      return;
    }

    const { id, value, type, checked } = e.target;

    const sanitizedValue =
      ["firstName", "lastName"].includes(id) && type === "text"
        ? alphabetChecker(value)
        : value;

    setUserData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : sanitizedValue,
    }));
  };

  const handleSocialChange = (e) => {
    const { id, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      socials: {
        ...prev.socials,
        [id]: value,
      },
    }));
  };

  const signatureRef = useRef(null);
  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const undoSignature = () => {
    const data = signatureRef.current?.toData();
    if (data && data.length) {
      data.pop();
      signatureRef.current?.fromData(data);
    }
  };

  const handleEndStroke = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setUserData((prev) => ({ ...prev, signature: signatureData }));
    }
  };

  const [errorMessage, setErrorMessage] = useState("");
  const validateForm = () => {
    const {
      firstName,
      lastName,
      email,
      country,
      socials,
      signature,
      tos,
      informationAccurate,
      contentId,
    } = userData;

    // Basic Info
    if (!firstName) {
      setErrorMessage("First Name is required.");
      return false;
    }

    if (!lastName) {
      setErrorMessage("Last Name is required.");
      return false;
    }

    if (!email) {
      setErrorMessage("Email is required.");
      return false;
    }

    if (!isValidEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }

    if (!country) {
      setErrorMessage("Country is required.");
      return false;
    }

    // Socials - at least one required
    const hasAtLeastOneLink = Object.values(socials).some(
      (link) => link.trim() !== ""
    );

    if (!hasAtLeastOneLink) {
      setErrorMessage("Please provide at least one channel link.");
      return false;
    }

    const invalidLinks = Object.entries(socials)
      .filter(([_, link]) => link.trim() !== "")
      .filter(([_, link]) => !isValidUrl(link));

    if (invalidLinks.length > 0) {
      const [platform] = invalidLinks[0];
      setErrorMessage(`Please enter a valid URL for ${platform}`);
      return false;
    }

    // Final Agreement
    if (!signature) {
      setErrorMessage("Signature is required.");
      return false;
    }

    if (!tos) {
      setErrorMessage("You must accept the Terms of Service.");
      return false;
    }

    if (!contentId) {
      setErrorMessage(
        "You must agree to not use the YouTube Copyright Match Tool to continue."
      );
      return false;
    }

    if (!informationAccurate) {
      setErrorMessage(
        "You must confirm your information is accurate to continue."
      );
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const [successToast, setSuccessToast] = useState(false);
  const [toastType, setToastType] = useState(false);
  const [errorPrompt, setErrorPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const [ipAddress, setIpAddress] = useState("");
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIp();
  }, []);

  const handleSignUp = async () => {
    if (!validateForm()) return;
    console.log(userData);
    creatorshieldContract(userData, ipAddress, userData.signature);
    if (!recaptchaToken) {
      setErrorPrompt("Please verify you're not a robot.");
      return;
    } else {
      setErrorPrompt("");
    }

    const userDetails = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      contact: userData.contact,
      country: userData.country,
      socials: userData.socials,
    };

    const formData = new FormData();
    formData.append("Email", userDetails.email);
    formData.append("FirstName", userDetails.firstName);
    formData.append("LastName", userDetails.lastName);
    formData.append("Country", userDetails.country);
    formData.append("IsCs", true);

    const socialLinks = Object.values(userDetails.socials).filter(
      (link) => link && link.trim() !== ""
    );
    socialLinks.forEach((link, index) => {
      formData.append(`Link[${index}]`, link);
    });

    const file = creatorshieldContract(
      userDetails,
      ipAddress,
      userData.signature
    );
    formData.append("ContractFile", file);

    try {
      setIsLoading(true);

      const result = await VVVSignUp(formData);
      if (result.success) {
        setSuccessToast(true);
        setToastType("success");
        setIsLoading(false);
        setUserData({
          firstName: "",
          lastName: "",
          email: "",
          contact: "",
          country: "",
          socials: {
            youtube: "",
            tiktok: "",
            facebook: "",
            instagram: "",
            twitchTv: "",
            douyin: "",
            kuaishou: "",
            rumble: "",
            others: "",
          },
          signature: "",
          tos: false,
          contentId: false,
          informationAccurate: false,
        });
        setRecaptchaToken("");
        signatureRef.current?.clear();
      } else {
        // Show error toast
        setSuccessToast(true);
        setToastType("error");
        setIsLoading(false);
        setErrorMessage(result.message || "Failed to submit. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting sign-up:", err);
      setIsLoading(false);
      setSuccessToast(true);
      setToastType("error");
      setErrorMessage("An unexpected error occurred. Please try again.");
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <div className="space-y-12 pt-28 lg:pt-40 bg-white">
        <div className="flex flex-col justify-center items-center px-6">
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2 mt-[14px] lg:mt-[-30px]">
              <img src={ZapIcon} alt="Zap Icon" className="w-5 h-5" />
              <span className="font-normal leading-normal whitespace-nowrap text-[#FF6B35]">
                Quick Setup - 2 Minutes
              </span>
            </div>
          </div>

          <div ref={headingRef} className="animate-fade-rise">
            <h1 className="text-5xl xl:text-6xl mb-6 text-center font-semibold text-[#FF6B35] px-4 max-w-2xl">
              Start Protecting Your Content Today
            </h1>
            <p className="text-lg max-w-xl mx-auto text-center mb-12">
              Join 4,500+ creators who are earning passive income by protecting
              their content from unauthorized reuploads.
            </p>
          </div>

          <div className="flex flex-col justify-center items-center w-80 md:w-[30rem] xl:w-[50rem] z-40">
            <PersonalInformation
              userData={userData}
              handleUserDataInput={handleUserDataInput}
            />
            <ChannelLinks
              userData={userData}
              handleSocialChange={handleSocialChange}
            />
            <Sign
              signatureRef={signatureRef}
              userData={userData}
              setUserData={setUserData}
              clearSignature={clearSignature}
              undoSignature={undoSignature}
              handleEndStroke={handleEndStroke}
              isLoading={isLoading}
              handleSignUp={handleSignUp}
              errorMessage={errorMessage}
              setRecaptchaToken={setRecaptchaToken}
              errorPrompt={errorPrompt}
            />

            {errorPrompt && (
              <div className="text-white bg-red-500 w-fit px-4 py-1 rounded-md mx-auto">
                {errorPrompt}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      <ToastModal
        showToast={successToast}
        setShowToast={setSuccessToast}
        form="esign"
        type={toastType}
      />
    </>
  );
}
